import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import type React from "react";

// Owned numeric input primitive.
//
// Only components/primitives may import @base-ui/react; see importBoundary.test.ts
// and docs/ui-boundary-migration-plan.md.
//
// The public API exposes values, never library event shapes. Base UI's
// onValueChange takes a second `eventDetails` argument carrying its own reason
// vocabulary; forwarding it would leak the implementation detail this boundary
// exists to contain, so it is dropped.
//
// No Increment or Decrement part is rendered. The replaced library drew spinner
// buttons and the application hid them with `span.react-numeric-input b {
// display: none }`, so the control has always presented as a plain text input.
// Not rendering them reproduces that without needing a rule to undo it.

export type NumberFieldProps = {
  value?: number | null;
  defaultValue?: number;
  min?: number;
  max?: number;
  /** Amount to step with arrow keys. `"any"` disables native step validation. */
  step?: number | "any";
  /**
   * Decimal places. Governs both the rendered text and the reported value, so a
   * consumer never has to trim the number it is handed.
   *
   * Values are rounded, not truncated: 12.349 at precision 2 reports 12.35. That
   * matches the replaced library, which applied `toFixed(precision)` before
   * emitting. The manual string truncation at its call sites looked like the
   * policy but was a no-op on an already-rounded number.
   */
  precision?: number;
  /** Render a trailing percent sign without scaling the value. */
  percent?: boolean;
  /** Accessible name, when the call site renders no visible label. */
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  placeholder?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
  onValueChange?: (value: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
};

/**
 * Base UI formats through Intl rather than a callback, so the replaced library's
 * `format={(n) => `${n}%`}` maps to the `unit` style. Note that `style: "percent"`
 * would be wrong: it multiplies by 100, turning 50 into "5,000%". Grouping is off
 * and no minimum fraction digits are set, so 50 renders as "50%" and 12.5 as
 * "12.5%", exactly as the string template did.
 */
function numberFormat(precision: number | undefined, percent: boolean | undefined): Intl.NumberFormatOptions {
  return {
    maximumFractionDigits: precision ?? 20,
    useGrouping: false,
    ...(percent ? { style: "unit" as const, unit: "percent" } : {}),
  };
}

export default function NumberField({
  className,
  defaultValue,
  disabled,
  id,
  label,
  max,
  min,
  name,
  onBlur,
  onFocus,
  onKeyDown,
  onValueChange,
  percent,
  placeholder,
  precision,
  readOnly,
  required,
  step,
  value,
  ...aria
}: NumberFieldProps): React.ReactElement {
  return (
    <BaseNumberField.Root
      data-slot="number-field-root"
      defaultValue={defaultValue}
      disabled={disabled}
      format={numberFormat(precision, percent)}
      id={id}
      max={max}
      min={min}
      name={name}
      onValueChange={onValueChange && ((next) => onValueChange(next))}
      readOnly={readOnly}
      required={required}
      // Decimal precision controls formatting, not step validation. Base UI's
      // default step of 1 otherwise makes its hidden input reject fractions.
      step={step ?? "any"}
      value={value}
    >
      <BaseNumberField.Input
        aria-describedby={aria["aria-describedby"]}
        aria-invalid={aria["aria-invalid"]}
        aria-label={label}
        aria-required={aria["aria-required"]}
        className={className}
        data-slot="number-field-input"
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
    </BaseNumberField.Root>
  );
}
